import { HttpTypes } from "@medusajs/types"

export type GlsDeliveryPoint = {
  id: string | number
  matchcode: string
  legacyId?: string
  type: number
  name: string
  street: string
  houseNumber?: string
  city: string
  zipCode: string
  countryIsoCode: string
  latitude?: number
  longitude?: number
  isActive?: boolean
  dropoffPoint?: boolean
  codHandler?: boolean
}

export type GlsDeliveryPointsResponse = {
  country_code: string
  count: number
  is_changed: boolean
  last_update_time?: string
  delivery_points: GlsDeliveryPoint[]
}

export function getShippingOptionData(
  option: HttpTypes.StoreCartShippingOption | undefined
): Record<string, unknown> | null {
  const data = (option as { data?: Record<string, unknown> | null } | undefined)?.data
  return data && typeof data === "object" ? data : null
}

export function isGlsParcelShopOption(
  option: HttpTypes.StoreCartShippingOption | undefined
): boolean {
  const data = getShippingOptionData(option)
  if (data?.delivery_mode === "parcelshop") {
    return true
  }

  return Boolean(option?.name?.toLowerCase().includes("prevzemno"))
}

export function isGlsHomeOption(
  option: HttpTypes.StoreCartShippingOption | undefined
): boolean {
  const data = getShippingOptionData(option)
  if (data?.delivery_mode === "home") {
    return true
  }

  const name = option?.name?.toLowerCase() || ""
  return name.includes("gls") && name.includes("dom")
}

export function isGlsOption(
  option: HttpTypes.StoreCartShippingOption | undefined
): boolean {
  if (!option) {
    return false
  }

  return isGlsHomeOption(option) || isGlsParcelShopOption(option)
}

export function hasGlsShippingOptions(
  options: HttpTypes.StoreCartShippingOption[] | null | undefined
): boolean {
  return (options || []).some(isGlsOption)
}

export function getStoredGlsDeliveryPoint(
  cart: HttpTypes.StoreCart | null | undefined
): GlsDeliveryPoint | null {
  const metadata = cart?.metadata as Record<string, unknown> | undefined
  const point = metadata?.gls_delivery_point

  if (!point || typeof point !== "object") {
    return null
  }

  const stored = point as GlsDeliveryPoint
  return stored.matchcode ? stored : null
}

export function formatGlsDeliveryPointLabel(point: GlsDeliveryPoint): string {
  const address = [point.street, point.houseNumber].filter(Boolean).join(" ")
  return `${point.name} · ${address}, ${point.zipCode} ${point.city}`
}

export function getGlsDeliveryPointTypeLabel(type: number): string {
  if (type === 2) {
    return "Paketomat"
  }

  if (type === 1) {
    return "Prevzemno mesto"
  }

  return "GLS točka"
}

export function getOpenStreetMapUrl(point: GlsDeliveryPoint): string | null {
  if (point.latitude == null || point.longitude == null) {
    return null
  }

  return `https://www.openstreetmap.org/?mlat=${point.latitude}&mlon=${point.longitude}#map=16/${point.latitude}/${point.longitude}`
}

export function filterGlsDeliveryPoints(
  points: GlsDeliveryPoint[],
  query: string
): GlsDeliveryPoint[] {
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return points
  }

  return points.filter((point) => {
    const haystack = [
      point.name,
      point.street,
      point.city,
      point.zipCode,
      point.matchcode,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()

    return haystack.includes(normalized)
  })
}

export function normalizePostalCode(value: string | null | undefined): string {
  return (value || "").trim().replace(/\s/g, "")
}

export function filterGlsPointsByPostalCode(
  points: GlsDeliveryPoint[],
  postalCode: string | null | undefined
): GlsDeliveryPoint[] {
  const normalizedPostalCode = normalizePostalCode(postalCode)
  if (!normalizedPostalCode) {
    return []
  }

  const exactMatches = points.filter(
    (point) => normalizePostalCode(point.zipCode) === normalizedPostalCode
  )

  if (exactMatches.length) {
    return exactMatches
  }

  if (normalizedPostalCode.length >= 3) {
    const prefix = normalizedPostalCode.slice(0, 3)
    return points.filter((point) =>
      normalizePostalCode(point.zipCode).startsWith(prefix)
    )
  }

  return []
}

export function getCartPostalCode(
  cart: HttpTypes.StoreCart | null | undefined
): string | null {
  const postalCode = cart?.shipping_address?.postal_code
  return postalCode ? normalizePostalCode(postalCode) : null
}

export type GeoCoordinate = {
  lat: number
  lng: number
}

export type GlsDeliveryPointWithDistance = GlsDeliveryPoint & {
  distanceKm?: number
}

export type GlsPointTypeFilter = 1 | 2 | null

const EARTH_RADIUS_KM = 6371

export function hasGeoCoordinates(
  point: GlsDeliveryPoint
): point is GlsDeliveryPoint & { latitude: number; longitude: number } {
  return point.latitude != null && point.longitude != null
}

export function haversineDistanceKm(
  from: GeoCoordinate,
  to: GeoCoordinate
): number {
  const toRadians = (value: number) => (value * Math.PI) / 180
  const latDelta = toRadians(to.lat - from.lat)
  const lngDelta = toRadians(to.lng - from.lng)
  const fromLat = toRadians(from.lat)
  const toLat = toRadians(to.lat)

  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(fromLat) * Math.cos(toLat) * Math.sin(lngDelta / 2) ** 2

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function sortGlsPointsByDistance(
  points: GlsDeliveryPoint[],
  origin: GeoCoordinate | null
): GlsDeliveryPointWithDistance[] {
  if (!origin) {
    return points
  }

  return [...points]
    .map((point) => {
      if (!hasGeoCoordinates(point)) {
        return { ...point, distanceKm: undefined }
      }

      return {
        ...point,
        distanceKm: haversineDistanceKm(origin, {
          lat: point.latitude,
          lng: point.longitude,
        }),
      }
    })
    .sort((left, right) => {
      if (left.distanceKm == null && right.distanceKm == null) {
        return 0
      }
      if (left.distanceKm == null) {
        return 1
      }
      if (right.distanceKm == null) {
        return -1
      }
      return left.distanceKm - right.distanceKm
    })
}

export function formatDistanceKm(distanceKm?: number): string | null {
  if (distanceKm == null || Number.isNaN(distanceKm)) {
    return null
  }

  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`
  }

  return `${distanceKm.toFixed(1)} km`
}

export function filterGlsPointsByType(
  points: GlsDeliveryPoint[],
  typeFilter: GlsPointTypeFilter
): GlsDeliveryPoint[] {
  if (typeFilter == null) {
    return points
  }

  return points.filter((point) => point.type === typeFilter)
}

export function getMapBounds(
  points: GlsDeliveryPoint[],
  homeCoordinate: GeoCoordinate | null
): GeoCoordinate[] {
  const coordinates: GeoCoordinate[] = []

  if (homeCoordinate) {
    coordinates.push(homeCoordinate)
  }

  points.filter(hasGeoCoordinates).forEach((point) => {
    coordinates.push({ lat: point.latitude, lng: point.longitude })
  })

  return coordinates
}

export function formatCartShippingAddress(
  cart: HttpTypes.StoreCart | null | undefined
): string | null {
  const address = cart?.shipping_address
  if (!address) {
    return null
  }

  const parts = [
    address.address_1,
    address.address_2,
    address.postal_code,
    address.city,
    address.country_code?.toUpperCase(),
  ].filter(Boolean)

  return parts.length ? parts.join(", ") : null
}

export function formatCartShippingAddressFallbacks(
  cart: HttpTypes.StoreCart | null | undefined
): string[] {
  const address = cart?.shipping_address
  if (!address) {
    return []
  }

  const full = formatCartShippingAddress(cart)
  const cityPostal = [address.postal_code, address.city, address.country_code?.toUpperCase()]
    .filter(Boolean)
    .join(", ")
  const postalOnly = [address.postal_code, address.country_code?.toUpperCase()]
    .filter(Boolean)
    .join(", ")

  return [full, cityPostal, postalOnly].filter(
    (value, index, array): value is string =>
      Boolean(value) && array.indexOf(value) === index
  )
}
